'use client';

import './article-template.scss';

import * as z from 'zod';
import type { ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { keys } from '@sola/contentful-model/keys.ts';
import { articlesUrlBase } from '@sola/contentful-model/lib/routes.ts';
import { programCountryLabels } from '@sola/contentful-model/lib/sac-values.ts';
import type { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.ts';
import type { ArticleProps } from '@sola/schema/lib/schema.ts';
import { submitSubscription } from '@sola/skippy-sdk/requests.ts';
import {
  TrackFunnelPage,
  TrackViewContent,
} from '@sola/tracking/components/datalayer.tsx';
import { slugify } from '@sola/utils/string.ts';

import { Image } from '../../atoms/image/image.tsx';
import { ShareButtons } from '../../molecules/share-buttons/share-buttons.tsx';
import { AccordionMain } from '../../organisms-cms/accordion/main/accordion-main.tsx';
import { ArticleListInsert } from '../../organisms-cms/article-list/insert/article-list-insert.tsx';
import { BasicContentMain } from '../../organisms-cms/basic-content/main/basic-content-main.tsx';
import { BasicContentSolo } from '../../organisms-cms/basic-content/solo/basic-content-solo.tsx';
import { DonateShim } from '../../organisms-cms/donate/shim/donate-shim.tsx';
import { GalleryCinema } from '../../organisms-cms/gallery/cinema/gallery-cinema.tsx';
import { JumbotronNotable } from '../../organisms-cms/jumbotron/notable/jumbotron-notable.tsx';
import { JumbotronSidekick } from '../../organisms-cms/jumbotron/sidekick/jumbotron-sidekick.tsx';
import { SacListInsert } from '../../organisms-cms/sac-list/insert/sac-list-insert.tsx';
import { FlexibleSection } from '../../organisms/flexible-section/flexible-section.tsx';
import { FormEmailSubscribe } from '../../organisms/forms/form-email-subscribe/form-email-subscribe.tsx';
import { handleAnchorClick } from '../../utils/anchor.ts';
import {
  FSmeta,
  FSmetaFullBleed,
  FSmetaFullBleedLight,
} from '../../utils/section-meta.ts';

interface ArticleTemplateProps {
  article: ArticleProps;
  experiments?: string[];
}

const ArticleSectionsEnum = z.enum(['cta', 'related', 'faq']);
type ArticleSectionsEnum = z.infer<typeof ArticleSectionsEnum>;

const ArticleJumpNav = ({ sections, taxonomyArticleType }: ArticleProps) => {
  const { t } = useTranslation(['articles', 'geo']);

  return (
    <div className="Article__sidebar-box pb-comfy-xl grid gap-comfy-sm">
      <h2 className="heading heading-xs heading-amplified">
        {taxonomyArticleType === keys.articleTypes.crisisUpdate
          ? t('articles:updates')
          : t('articles:tableOfContents')}
      </h2>
      <div className="Article__sections flex flex-col gap-comfy-xs">
        {sections?.map((section) => (
          <Link
            key={section.id}
            href={`#${section.id}`}
            onClick={(e) => handleAnchorClick(e, `#${section.id}`)}
          >
            <h3 className="label-lg label-potent">{section.title}</h3>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const ArticleTemplate: React.FC<ArticleTemplateProps> = ({
  article,
  experiments,
}) => {
  const { t } = useTranslation(['articles', 'core', 'geo']);

  const articleBase = article.taxonomyArticleType
    ? articlesUrlBase[article.taxonomyArticleType as ArticleTypesEnum]
    : '#';

  const renderTaxonomyTags = (firstOnly = false) => {
    const tags: ReactNode[] = [];

    // Type
    tags.push(
      <Link
        key="type"
        href={`${articleBase}`}
        title={t(
          `articles:taxonomy.articleTypes.${article.taxonomyArticleType}.singularName`,
        )}
      >
        <Badge
          label={t(
            `articles:taxonomy.articleTypes.${article.taxonomyArticleType}.singularName`,
            'articles:taxonomy.articleTypes.type.singularName',
          )}
        />
      </Link>,
    );

    // Topics
    const topics = firstOnly
      ? article.taxonomyTopic?.slice(0, 1)
      : article.taxonomyTopic;
    if (topics) {
      for (const topic of topics) {
        tags.push(
          <Link
            key={`topic-${topic}`}
            href={`${articleBase}/tag/${slugify(topic)}`}
            title={t(`articles:taxonomy.articleTopics.${topic}.name`)}
          >
            <Badge
              label={t(
                `articles:taxonomy.articleTopics.${topic}.name`,
                'articles:taxonomy.articleTopics.topic.name',
              )}
            />
          </Link>,
        );
      }
    }

    // Countries
    const countries = firstOnly
      ? article.taxonomyProgramCountry?.slice(0, 1)
      : article.taxonomyProgramCountry;
    if (countries) {
      for (const country of countries) {
        const countryLabel =
          programCountryLabels[country as keyof typeof programCountryLabels];
        tags.push(
          <Link
            key={`country-${country}`}
            href={`${articleBase}/tag/${slugify(countryLabel)}`}
            title={t(`geo:countries.${country}.nameS`)}
          >
            <Badge label={t(`geo:countries.${country}.nameS`)} />
          </Link>,
        );
      }
    }

    // Themes
    const themes = firstOnly
      ? article.taxonomyArticleTheme?.slice(0, 1)
      : article.taxonomyArticleTheme;
    if (themes) {
      for (const theme of themes) {
        tags.push(
          <Link
            key={`theme-${theme}`}
            href={`${articleBase}/tag/${slugify(theme)}`}
            title={t(`articles:taxonomy.articleThemes.${theme}.name`)}
          >
            <Badge
              label={t(
                `articles:taxonomy.articleThemes.${theme}.name`,
                'articles:taxonomy.articleThemes.theme.name',
              )}
            />
          </Link>,
        );
      }
    }

    return tags;
  };

  // controls section colors
  const activeSections: ArticleSectionsEnum[] = [];
  if (article.secondaryCta) {
    activeSections.push(ArticleSectionsEnum.enum.cta);
  }

  activeSections.push(ArticleSectionsEnum.enum.related);

  if (article.faq) {
    activeSections.push(ArticleSectionsEnum.enum.faq);
  }

  const getSectionMeta = (section: string) => {
    const index = activeSections.indexOf(section as ArticleSectionsEnum);
    return {
      ...(index % 2 === 0 ? FSmetaFullBleedLight : FSmetaFullBleed),
      anchor: section,
    };
  };

  const hasJumpList = article.sections && article.sections.length > 0;

  return (
    <div className="Article Article--blog ">
      <TrackFunnelPage funnel={article.taxonomyFunnel} />
      <TrackViewContent
        cmsId={article.contentfulEntryID!}
        title={article.title}
        type={article.taxonomyArticleType}
        tags={[
          ...(article.taxonomyArticleTheme ?? []),
          ...(article.taxonomyTopic ?? []),
        ]}
      />
      <FlexibleSection
        meta={{ ...FSmeta, anchor: 'intro' }}
        children1={
          <div className="Article__intro">
            <BasicContentSolo
              seoLevel={1}
              subtitle={
                article.publishDate
                  ? new Date(article.publishDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      timeZone: 'UTC',
                    })
                  : undefined
              }
              title={article.title || undefined}
              body={<p>{article.summary}</p>}
            />

            <div className="Article__metabar flex justify-between w-full">
              <div className="Article__tags flex flex-wrap gap-snug-xl">
                {renderTaxonomyTags(true)}
              </div>
              <div className="Article__share">
                <ShareButtons
                  shareTitle={article.title}
                  shareSummary={article.summary}
                />
              </div>
            </div>

            {article.primaryMedia?.items && (
              <div className="Article__primary-media">
                {/* seoLevel 1 marks this as the primary media for the page, so
                    its image renders server-side as the LCP candidate. Matches
                    how the CMS assembler levels the first organism. */}
                <GalleryCinema {...article.primaryMedia} seoLevel={1} />
              </div>
            )}
          </div>
        }
      />
      <FlexibleSection
        meta={{
          ...FSmeta,
          overlap:
            article.primaryMedia?.items &&
            article.primaryMedia.items.length > 0,
          anchor: 'article',
          layoutRatio: '2-1',
        }}
        featureOnMobile={
          hasJumpList ? <ArticleJumpNav {...article} /> : undefined
        }
        children1={
          <article className="Article__article body-md flex flex-col gap-comfy-xl">
            {article.intro && (
              <div className="Article__card rounded-lg p-comfy-lg @md:p-comfy-xl">
                <BasicContentMain seoLevel={2} body={article.intro} inline />
              </div>
            )}

            <BasicContentMain
              seoLevel={2}
              body={article.body || undefined}
              inline
            />

            {article.conclusion &&
              article.taxonomyArticleType !==
                keys.articleTypes.crisisUpdate && (
                <div className="Article__card rounded-lg p-comfy-lg @md:p-comfy-xl">
                  <BasicContentMain
                    seoLevel={2}
                    body={article.conclusion}
                    inline
                  />
                </div>
              )}

            {article.conclusion &&
              article.taxonomyArticleType ===
                keys.articleTypes.crisisUpdate && (
                <BasicContentMain
                  seoLevel={2}
                  body={article.conclusion}
                  inline
                />
              )}

            <div className="Article__footer flex flex-row justify-between">
              <div className="Article__tags mt-comfy-xs flex flex-wrap gap-snug-xl">
                {renderTaxonomyTags(false)}
              </div>
              <div className="Article__share">
                <ShareButtons
                  shareTitle={article.title}
                  shareSummary={article.summary}
                />
              </div>
            </div>
          </article>
        }
        children2={
          <aside className="Article__sidebar flex flex-col gap-comfy-xl">
            {hasJumpList && (
              <div className="Article__sidebar-sections Article__sidebar-box">
                <ArticleJumpNav {...article} />
              </div>
            )}
            {article.taxonomyArticleType !== keys.articleTypes.pressRelease && (
              <div className="Article__sidebar-group flex flex-col gap-comfy-xl @sm:flex-row @sm:items-center @lg:flex-col @lg:items-start">
                {article.contributors && article.contributors.length > 0 && (
                  <div className="Article__sidebar-box flex-1 pb-comfy-xl grid gap-comfy-sm">
                    <h2 className="heading heading-xs">
                      {t('articles:contributors')}
                    </h2>
                    <div className="Article__contributors flex flex-col gap-comfy-sm">
                      {article.contributors.map((contributor) => (
                        <div
                          key={contributor.name}
                          className="label-lg label-moderate flex gap-comfy-xs"
                        >
                          {contributor.image?.url && (
                            <Image
                              image={{
                                url: contributor.image.url,
                                alt: contributor.name,
                              }}
                              aspectRatio="1"
                              rounded="full"
                              className="Contributor__image"
                            />
                          )}
                          <div className="Contributor__info flex flex-col">
                            <h3 className="Contributor__heading label-md label-potent">
                              {contributor.name}
                            </h3>
                            <p>{contributor.position}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* // @TODO: SUBSCRIBE FORM */}
                <div className="Article__sidebar-box flex-1 pb-comfy-xl grid gap-comfy-sm">
                  <FormEmailSubscribe
                    className="max-w-md"
                    title={t('articles:subscribeTitle')}
                    subscriptionCode="Leads_General_Sign_Up"
                    onSubmit={submitSubscription}
                    subscribeDirectionForm="column"
                    showInputsPlaceholder
                    titleSize="xs"
                  />
                </div>
              </div>
            )}

            {article.taxonomyArticleType === keys.articleTypes.pressRelease && (
              <div className="Article__sidebar-group flex flex-col gap-comfy-xl @sm:flex-row @sm:items-center @lg:flex-col @lg:items-start">
                <div className="Article__sidebar-box flex-1 pb-comfy-xl grid gap-comfy-sm border-b-1">
                  <h2 className="heading heading-xs pb-snug-lg">
                    {t('articles:aboutCompassionTitle')}
                  </h2>
                  <div className="flex flex-col gap-comfy-xs body body-sm">
                    {t('articles:aboutCompassionBody')}
                  </div>
                </div>
                <div className="Article__sidebar-box flex-1 pb-comfy-xl grid gap-comfy-sm">
                  <h2 className="heading heading-xs">
                    {t('core:sharing.follow')}
                  </h2>

                  {/* TODO: translate link titles */}
                  <div className="flex flex-col gap-comfy-lg">
                    <Link
                      href="https://www.facebook.com/compassionintl"
                      target="blank"
                      title="Compassion International - Facebook"
                    >
                      <ArkIcon iconName="facebook" size="24" />
                      {t('core:sharing.facebook')}
                    </Link>
                    <Link
                      href="https://twitter.com/compassion"
                      target="blank"
                      title="Compassion International - X (Twitter)"
                    >
                      <ArkIcon iconName="twitter" size="24" />
                      {t('core:sharing.twitter')}
                    </Link>
                    <Link
                      href="https://instagram.com/compassion"
                      target="blank"
                      title="Compassion International - Instagram"
                    >
                      <ArkIcon iconName="instagram" size="24" />
                      {t('core:sharing.instagram')}
                    </Link>
                    <Link
                      href="https://www.tiktok.com/@compassioninternational"
                      target="blank"
                      title="Compassion International - TikTok"
                    >
                      <ArkIcon iconName="tiktok" size="24" />
                      {t('core:sharing.tiktok')}
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {article.primaryCta &&
              ('fund' in article.primaryCta ? (
                <DonateShim {...{ ...article.primaryCta, body: undefined }} />
              ) : (
                <JumbotronNotable {...article.primaryCta} />
              ))}
          </aside>
        }
      />
      {activeSections.includes(ArticleSectionsEnum.enum.cta) &&
        article?.secondaryCta && (
          <FlexibleSection
            meta={getSectionMeta(ArticleSectionsEnum.enum.cta)}
            children1={
              'limit' in article.secondaryCta ? (
                <SacListInsert {...article.secondaryCta} />
              ) : (
                <JumbotronSidekick seoLevel={2} {...article.secondaryCta} />
              )
            }
          />
        )}
      {activeSections.includes(ArticleSectionsEnum.enum.related) && (
        <FlexibleSection
          meta={getSectionMeta(ArticleSectionsEnum.enum.related)}
          children1={
            <>
              <BasicContentSolo
                seoLevel={2}
                title={t('articles:relatedContent')}
              />
              <ArticleListInsert seoLevel={3} relatedArticle={article} />
            </>
          }
        />
      )}
      {activeSections.includes(ArticleSectionsEnum.enum.faq) && (
        <FlexibleSection
          meta={getSectionMeta(ArticleSectionsEnum.enum.faq)}
          children1={<AccordionMain seoLevel={2} {...article.faq} />}
        />
      )}
    </div>
  );
};
